import CheckIcon from '@mui/icons-material/Check';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { Box, FormControl, Grid } from '@mui/material/';
import { useFormik } from 'formik';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useLogin } from '@/api/mutations/useAuthMutations';
import { useUserQuery } from '@/api/queries/useAuth';
import { FormHelperText } from '@/components/FormHelperText/FormHelperText';
import { LogoSvg } from '@/components/Icons/LogoSvg';
import { Loading } from '@/components/Loading/Loading';
import { Button } from '@/components/ui/button';
import { paths } from '@/constants';
import { useToken } from '@/hooks';
import { PREFERENCE_DEFAULTS, useChangeUser, usePrefValue } from '@/preferences';
import type { LoginType } from '@/types';
import { isApiError } from '@/types';
import { parseJwt } from '@/utils/functions';
import { validateLogin } from '@/utils/validators';
import { InputLogin, InputTitleTypography, MailLoginTypography } from './LoginForm.styles';
import { LoginWrap } from './LoginWrap';

export const LoginForm = () => {
    const { t } = useTranslation();
    const validate = validateLogin(t);
    const navigate = useNavigate();
    const changeUser = useChangeUser();
    const { data: userData } = useUserQuery();
    const id = userData?.id;
    const last_page = usePrefValue('last_page');
    const { mutateAsync: login, status } = useLogin();
    const [visiblePassword, setVisiblePassword] = useState(false);

    const token = useToken();

    if (token || localStorage.getItem('token')) {
        window.location.pathname = paths.profile.index;
    }

    const initialValues: LoginType = {
        email: '',
        password: '',
    };

    const formik = useFormik({
        initialValues,
        validate,
        validateOnMount: true,
        isInitialValid: false,
        onSubmit: async values => {
            try {
                const { success, data } = await login(values);
                if (success) {
                    const parsed = parseJwt(data.token);
                    if (parsed && Number(parsed.sub) === id && last_page) {
                        return navigate(last_page);
                    } else if (parsed) {
                        changeUser(PREFERENCE_DEFAULTS);
                        navigate(paths.profile.index);
                    }
                }
            } catch (error) {
                formik.setSubmitting(false);
                if (isApiError(error)) {
                    formik.setErrors({ password: error.data.message });
                }
            }
        },
    });

    return (
        <LoginWrap>
            <Box
                display="inline-block"
                sx={theme => ({
                    mb: '60px',
                    [theme.breakpoints.down('lg')]: {
                        mb: '40px',
                        maxWidth: '205px',
                        svg: {
                            maxWidth: '100%',
                            height: 'auto',
                        },
                    },
                })}
            >
                <LogoSvg />
            </Box>
            <form noValidate autoComplete="off" onSubmit={formik.handleSubmit}>
                <MailLoginTypography children={t('helpers.password.login-by-mail')} />
                <div className="flex flex-wrap w-full gap-[36px]">
                    <FormControl sx={{ maxWidth: '400px' }}>
                        <InputTitleTypography children={t('helpers.password.your-mail')} />
                        <InputLogin
                            id="email"
                            name="email"
                            type="text"
                            placeholder={t('placeholders.enter-email')}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.email}
                            endAdornment={
                                !formik.errors.email ? (
                                    <CheckIcon
                                        sx={{
                                            color: theme => (theme.palette.mode === 'dark' ? theme.palette.success.main : theme.custom.c25),
                                        }}
                                    />
                                ) : null
                            }
                        />
                        <FormHelperText error={formik.errors.email} touched={formik.touched.email} />
                    </FormControl>

                    <FormControl sx={{ maxWidth: '400px' }}>
                        <div className="flex flex-wrap w-full justify-between">
                            <InputTitleTypography children={t('password')} />
                            <Box
                                onClick={() => {
                                    if (!formik.errors.email && formik.values.email) {
                                        navigate(paths.sign.forgotPassword);
                                    }
                                }}
                                sx={{
                                    cursor: !formik.errors.email && formik.values.email ? 'pointer' : 'default',
                                    opacity: !formik.errors.email && formik.values.email ? 1 : 0.5,
                                }}
                            >
                                <InputTitleTypography
                                    children={t('helpers.password.forgot')}
                                    sx={[
                                        {
                                            textDecoration: 'underline',
                                            color: theme => {
                                                if (!formik.errors.email && formik.values.email) {
                                                    return theme.palette.primary.contrastText;
                                                }
                                                return theme.custom.c4; // Gray color for disabled state
                                            },
                                            '&:hover': {
                                                color: theme => {
                                                    if (!formik.errors.email && formik.values.email) {
                                                        return theme.palette.secondary.main; // Red text on hover when enabled
                                                    }
                                                    return theme.custom.c4; // Keep gray when disabled
                                                },
                                            },
                                        },
                                    ]}
                                />
                            </Box>
                        </div>
                        <InputLogin
                            id="password"
                            name="password"
                            type={visiblePassword ? 'text' : 'password'}
                            placeholder={t('placeholders.enter-password')}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.password}
                            error={!!formik.errors.password}
                            endAdornment={
                                <div className="flex gap-[16px] ml-[16px]">
                                    <Grid
                                        container
                                        onClick={() => {
                                            setVisiblePassword(!visiblePassword);
                                        }}
                                        sx={{ cursor: 'pointer' }}
                                    >
                                        {visiblePassword ? <VisibilityOff color="secondary" /> : <Visibility color="secondary" />}
                                    </Grid>
                                    {!formik.errors.password ? <CheckIcon color="success" /> : null}
                                </div>
                            }
                            sx={
                                formik.errors.password && formik.touched.password
                                    ? [
                                          {
                                              '&:after': {
                                                  content: '""',
                                                  position: 'absolute',
                                                  top: '0',
                                                  left: '0',
                                                  width: '100%',
                                                  height: '100%',
                                                  background: 'rgba(238, 91, 91, 0.12)',
                                              },
                                          },
                                      ]
                                    : []
                            }
                        />
                        <FormHelperText
                            error={formik.errors.password}
                            touched={formik.touched.password}
                            sx={{
                                color: theme => theme.palette.secondary.main,
                            }}
                        />
                    </FormControl>
                </div>
                <div className="flex flex-wrap w-full mt-[36px] gap-[24px]">
                    <Button
                        type="submit"
                        size="large"
                        disabled={!formik.isValid || formik.isSubmitting}
                        variant="contained"
                        className="w-full max-w-[400px]"
                    >
                        {status === 'pending' ? <Loading /> : t('buttons.entering')}
                    </Button>

                    {/* кнопка регистрации на будущее 
                             <Button
                                size="large"
                                variant="outlined"
                                sx={{
                                    maxWidth: "400px", width: "100%", '&:hover': {
                                        color: theme => theme.palette.secondary.main
                                    }
                                }}
                            >
                                {status === 'pending' ? <Loading /> : t('buttons.register')}
                            </Button> */}
                </div>
            </form>
        </LoginWrap>
    );
};
